import { useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { Tooltip } from 'react-tooltip';
import { MY_COACH_PLAYERS_PAGE } from 'constants/pages';
import {
  COACH_REFLECTION_ON_PLAYER_LESSON_API,
  PostCoachReflectionOnPlayerLessonPayload,
} from 'constants/payloads/coachReflectionOnPlayerLesson';
import { EMPTY_AVATAR_SRC } from 'constants/user';
import {
  LessonReflectionCommentTypesEnum,
  LessonSkillCategoriesEnum,
  LessonSkillsEnum,
  LessonTypesEnum,
  SportsEnum,
  UserProfiles,
  useGetCoachAllPlayersReflectionsLazyQuery,
} from 'types/generated/client';
import { trackEvent } from 'services/client/analytics';
import { ONBOARDING_EXAMPLE_REFLECTION_CLICKED } from 'services/client/analytics/events';
import { RemoteCoachTypesEnum } from 'utils/shared/coach/constants';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { useApiGateway } from 'hooks/useApi';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import AlertIcon from 'svg/AlertIcon';
import Location from 'svg/Location';
import NoteIcon from 'svg/NoteIcon';
import { AvatarProps } from 'screens/CoachLoggedInScreen/props';
import ReflectionFlowModal from 'screens/CoachPlayersTab/components/ReflectFlowModal';
import { ReflectionFormType } from 'screens/CoachPlayersTab/components/ReflectFlowModal/props';
import Button, { ButtonLink } from 'components/Button';
import CustomTable from 'components/CustomTable';
import { CustomTableProps } from 'components/CustomTable/props';
import { ProcessedReflection } from 'components/SkillAnalysis/props';
import useSkipReflection from 'components/SkillAnalysis/useSkipReflection';
import { processLessonReflections } from 'components/SkillAnalysis/utils';
import LessonBadge from 'components/tournaments/LessonBadge';
import { formatLessonDate, getDifferenceInHours, getLessonTypeDisplayLabel } from '../../utils';
import { OutstandingRecapsSkeleton } from '../Skeletons';

const ALERT_THRESHOLD_HOURS = 72; // 3 days

// Avatar component
const Avatar = ({ src, alt, className = '', style }: AvatarProps) => (
  <div
    className={`h-8 w-8 rounded-full bg-cover bg-center bg-no-repeat ${className}`}
    style={{ backgroundImage: `url('${src}')`, ...style }}
  />
);

// Get lesson type display with LessonBadge
const getLessonTypeDisplay = (lessonType: LessonTypesEnum) => {
  return (
    <LessonBadge label={getLessonTypeDisplayLabel(lessonType)} variant={lessonType} size="sm" />
  );
};

const OutstandingRecaps = () => {
  const viewer = useViewer();

  const reflectionModal = useModal();

  const [selectedPlayerForReflection, setSelectedPlayerForReflection] = useState<any>(null);

  const { post: postCoachReflection, isLoading: postCoachReflectionLoading } =
    useApiGateway<PostCoachReflectionOnPlayerLessonPayload>(COACH_REFLECTION_ON_PLAYER_LESSON_API);

  const [
    getCoachAllPlayersReflections,
    { loading: coachAllPlayersReflectionsLoading, data: coachAllPlayersReflectionsData },
  ] = useGetCoachAllPlayersReflectionsLazyQuery();

  const { SkipReflectionModalJsx, promptSkipReflection } = useSkipReflection({
    refetchQueries: ['getCoachAllPlayersReflections'],
  });
  const getAllPlayersReflections = useCallback(async () => {
    try {
      await getCoachAllPlayersReflections({
        variables: {
          coachId: viewer.userId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });
    } catch (err) {
      captureException(err);
    }
  }, [viewer.userId, getCoachAllPlayersReflections]);

  useEffect(() => {
    if (viewer.userId) {
      getAllPlayersReflections();
    }
  }, [viewer.userId, getAllPlayersReflections]);

  const reflectionsData = useMemo(() => {
    return processLessonReflections(coachAllPlayersReflectionsData?.lessons || [], true);
  }, [coachAllPlayersReflectionsData]);

  const pendingReflections = useMemo(() => {
    return reflectionsData.filter((reflection) => {
      // Check if lesson has no reflections (needs recap)
      return reflection.isCoachReflectionPending && !reflection.isCoachRecapSkipped;
    });
  }, [reflectionsData]);

  const handleReflectOnPlayer = (
    lessonId: string,
    lessonOwner?: UserProfiles | null,
    lessonDate?: string,
    selectedPlayer?: UserProfiles | null,
  ) => {
    trackEvent(ONBOARDING_EXAMPLE_REFLECTION_CLICKED, {
      source: 'onboarding',
      lessonId,
    });

    // Find the reflection data for this lesson
    const reflection = pendingReflections.find((reflection) => reflection.lessonId === lessonId);
    if (!reflection) return;

    // Set up the player for reflection (similar to CoachPlayersTab)
    const player = {
      id: reflection.player?.id || selectedPlayer?.id,
      fullName: reflection.player?.fullName || selectedPlayer?.fullName,
      pendingAction: {
        lessonId,
        coachLessonType: RemoteCoachTypesEnum.Court,
        lessonDate: lessonDate,
      },
    };

    setSelectedPlayerForReflection(player);
    reflectionModal.openModal();
  };

  const handleCloseReflectionFlowModal = () => {
    reflectionModal.isOpen && reflectionModal.closeModal();
  };

  const handleAddReflection = async (
    values: ReflectionFormType,
    reflectOnAllParticipants: boolean,
  ) => {
    try {
      const currentSkills =
        values.currentSkills?.map((val) => {
          const isCustomSkill = !Object.values(LessonSkillsEnum).includes(val as LessonSkillsEnum);
          return {
            skill: isCustomSkill ? null : (val as LessonSkillsEnum),
            customSkill: isCustomSkill ? val : '',
            userId: viewer.userId,
            type: LessonReflectionCommentTypesEnum.Completed,
          };
        }) || [];

      const futureSkills =
        values.futureSkills?.map((val) => {
          const isCustomSkill = !Object.values(LessonSkillsEnum).includes(val as LessonSkillsEnum);
          return {
            skill: isCustomSkill ? null : (val as LessonSkillsEnum),
            customSkill: isCustomSkill ? val : '',
            userId: viewer.userId,
            type: LessonReflectionCommentTypesEnum.Upcoming,
          };
        }) || [];

      const assignmentSkills =
        values.assignmentSkills?.map((val) => {
          const isCustomSkill = !Object.values(LessonSkillsEnum).includes(val as LessonSkillsEnum);
          return {
            skill: isCustomSkill ? null : (val as LessonSkillsEnum),
            customSkill: isCustomSkill ? val : '',
            userId: viewer.userId,
            type: LessonReflectionCommentTypesEnum.Assignment,
          };
        }) || [];

      const completedCategory =
        values.completedCategoryOrSport &&
        Object.values(LessonSkillCategoriesEnum).includes(
          values.completedCategoryOrSport as LessonSkillCategoriesEnum,
        )
          ? (values.completedCategoryOrSport as LessonSkillCategoriesEnum)
          : undefined;
      const upcomingCategory =
        values.upcomingCategoryOrSport &&
        Object.values(LessonSkillCategoriesEnum).includes(
          values.upcomingCategoryOrSport as LessonSkillCategoriesEnum,
        )
          ? (values.upcomingCategoryOrSport as LessonSkillCategoriesEnum)
          : undefined;
      const completedSport =
        values.completedCategoryOrSport &&
        Object.values(SportsEnum).includes(values.completedCategoryOrSport as SportsEnum)
          ? (values.completedCategoryOrSport as SportsEnum)
          : undefined;
      const upcomingSport =
        values.upcomingCategoryOrSport &&
        Object.values(SportsEnum).includes(values.upcomingCategoryOrSport as SportsEnum)
          ? (values.upcomingCategoryOrSport as SportsEnum)
          : undefined;

      const assignmentSport =
        values.assignmentCategoryOrSport &&
        Object.values(SportsEnum).includes(values.assignmentCategoryOrSport as SportsEnum)
          ? (values.assignmentCategoryOrSport as SportsEnum)
          : undefined;

      const assignmentCategory =
        values.assignmentCategoryOrSport &&
        Object.values(LessonSkillCategoriesEnum).includes(
          values.assignmentCategoryOrSport as LessonSkillCategoriesEnum,
        )
          ? (values.assignmentCategoryOrSport as LessonSkillCategoriesEnum)
          : undefined;

      const formattedPayload: PostCoachReflectionOnPlayerLessonPayload = {
        coachUserId: viewer.userId as string,
        playerUserId: selectedPlayerForReflection?.id,
        lessonId: selectedPlayerForReflection?.pendingAction?.lessonId,
        lessonReflectionComments: {
          data: [...currentSkills, ...futureSkills, ...assignmentSkills],
        },
        details: values.details,
        files: {
          data: values.lessonReflectionFiles || [],
        },
        completedCategory,
        completedSport,
        upcomingCategory,
        upcomingSport,
        assignmentCategory,
        assignmentSport,
        reflectOnAllParticipants,
      };

      await postCoachReflection({ payload: formattedPayload });
      await getAllPlayersReflections();
    } catch (err) {
      captureException(err);
      throw err;
    }
  };

  const columns: CustomTableProps<ProcessedReflection>['columns'] = [
    {
      title: 'Elapsed',
      columnClassName: 'w-[4rem]',
      render: (row) => {
        const differenceInHours = getDifferenceInHours(row.date);
        return (
          <div className="pl-2">
            {differenceInHours > ALERT_THRESHOLD_HOURS ? (
              <>
                <Tooltip
                  className="!rounded-xl !bg-brand-fire-500"
                  id={`alert-icon-tip-${row.lessonId}`}
                  content={`This lesson recap has been pending for more than ${Math.floor(
                    differenceInHours / 24,
                  )} days. Please provide the recap as quickly as possible!`}
                  place="top"
                />
                <AlertIcon
                  className="text-color-brand-primary"
                  data-tooltip-id={`alert-icon-tip-${row.lessonId}`}
                />
              </>
            ) : (
              <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                {differenceInHours}h
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Player',
      columnClassName: 'w-[11rem]',
      render: (row) => {
        return (
          <div className="flex items-center gap-3">
            <Avatar
              src={row.playerAvatarInfo?.imageSrc || EMPTY_AVATAR_SRC}
              alt={row.playerAvatarInfo?.name || 'Unknown'}
              className="h-8 w-8"
            />

            <div className="typography-product-chips-filters">
              {toTitleCase(row.playerAvatarInfo?.name || '')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Date',
      columnClassName: 'w-[8rem] max-sm:hidden',
      render: (row) => (
        <div className="typography-product-chips-filters text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {formatLessonDate(row.date)}
        </div>
      ),
    },
    {
      title: 'Location',
      columnClassName: 'max-md:hidden',
      render: (row) => (
        <div className="flex items-center gap-1">
          <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
          <span className="typography-product-chips-filters truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {row.address}
          </span>
        </div>
      ),
    },
    {
      title: 'Lesson type',
      columnClassName: 'max-xl:hidden',
      render: (row) => getLessonTypeDisplay(row.lessonType),
    },
    {
      title: 'Lesson title',
      columnClassName: 'max-lg:hidden',
      render: (row) => (
        <div className="typography-product-chips-filters text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {row.lessonTitle}
        </div>
      ),
    },
    {
      title: '',
      columnClassName: 'w-[12rem]',
      render: (row) => {
        return (
          <div className="ml-auto flex items-center gap-2 max-sm:flex-col">
            <Button
              onClick={() =>
                handleReflectOnPlayer(row.lessonId, row.lessonOwner!, row.date, row.player)
              }
              variant="primary"
              sizeDesktop="sm"
              sizeMobile="xs"
              className="max-w-fit"
              iconLeft={<NoteIcon className="h-4 w-4 text-color-text-lightmode-invert" />}
            >
              Recap
            </Button>
            <Button
              onClick={() =>
                promptSkipReflection({
                  playerId: row.player?.id || '',
                  playerName: row.player?.fullName || '',
                  lessonId: row.lessonId,
                })
              }
              variant="secondary"
              sizeDesktop="sm"
              sizeMobile="xs"
              className="max-w-fit"
            >
              Skip
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {coachAllPlayersReflectionsLoading ? (
        <OutstandingRecapsSkeleton />
      ) : (
        <div className="container mx-auto mt-8 max-sm:mt-ds-xl max-sm:px-4">
          <h5 className="typography-product-heading-compact-desktop max-sm:typography-product-subheading mb-ds-xl max-sm:mb-ds-md">
            Outstanding On-court Lesson Reviews
          </h5>

          {pendingReflections.length > 0 ? (
            <>
              <CustomTable columns={columns} data={pendingReflections.slice(0, 5)} shadow={true} />
              <div className="mt-6 flex justify-end max-sm:mt-4">
                <ButtonLink
                  href={MY_COACH_PLAYERS_PAGE}
                  variant="secondary"
                  sizeDesktop="md"
                  sizeMobile="sm"
                  className="w-fit"
                >
                  See More
                </ButtonLink>
              </div>
            </>
          ) : (
            <div className="flex h-32 items-center justify-center rounded-2xl border border-color-border-card-lightmode dark:border-color-border-card-darkmode">
              <div className="typography-product-chips-filters text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                No outstanding reviews
              </div>
            </div>
          )}
        </div>
      )}

      <ReflectionFlowModal
        selectedPlayerForReflection={selectedPlayerForReflection}
        handleClose={handleCloseReflectionFlowModal}
        isOpen={reflectionModal.isOpen}
        handleAddReflection={handleAddReflection}
        handleEditReflection={null}
        submitting={postCoachReflectionLoading}
        initialValues={null}
        activeLessonParticipantsCount={1}
        activeLessonPendingReflectionsCount={1}
      />

      {SkipReflectionModalJsx}
    </>
  );
};

export default OutstandingRecaps;
